<template>
  <div>
    <div
      class="collapse-title grid grid-cols-[30fr_70fr] items-center gap-x-4 gap-y-1"
      :class="{
        'md:grid-cols-[15fr_45fr_20fr_20fr]': !hidePrice,
        'md:grid-cols-[15fr_65fr_20fr]': hidePrice,
      }"
      data-testid="offer-card"
    >
      <img
        :src="offer.car.imageUrl ?? offer.car.vehicleType?.imageUrl ?? ''"
        :width="100"
        :height="60"
        class="max-sm:row-span-3"
      />
      <div>
        <h3>
          {{ offer.car.vehicleType?.make.name }}
          {{ offer.car.vehicleType?.model }}
        </h3>
        <div class="flex flex-wrap items-center gap-2">
          <LicencePlate v-if="offer.car.licencePlate && offer.car.licencePlate !== '-'">{{
            offer.car.licencePlate
          }}</LicencePlate>
          <span class="text-sm text-obsidian-500">{{ offer.car.fleetNumber }}</span>
        </div>
      </div>

      <div v-if="!hidePrice" class="flex flex-col gap-1 max-sm:col-start-2">
        <div class="flex justify-end gap-2">
          <span>{{ t('vehiclePrice') }}</span>
          <span class="font-bold">{{ formatCurrency(getTotalBasePrice(offer.pricing)) }}</span>
        </div>
        <div
          v-if="showTransferBreakdown && transferPriceTotal !== null"
          class="flex justify-end gap-2"
        >
          <span>{{ t('transferTotal') }}</span>
          <span class="font-bold">{{ formatCurrency(transferPriceTotal) }}</span>
        </div>
      </div>

      <CVButton
        :is-loading="isSelecting"
        :disabled="disabled"
        class="max-sm:col-start-2"
        @click.prevent="emit('onOfferSelect', offer)"
      >
        {{ t('select') }}
      </CVButton>
    </div>
    <div class="divider"></div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import type { Offer } from '@carvia/ros-client-types';
import { formatCurrency } from '../../../../utils/format-numbers.ts';
import LicencePlate from '@/components/LicencePlate.vue';
import { getTotalTransferPriceFromPricing } from '@/utils/get-total-transfer-price';
import { getTotalBasePrice } from '@/utils/pricing.utils';
import CVButton from '@/components/buttons/CVButton.vue';
const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    offer: Offer;
    hidePrice?: boolean;
    showTransferBreakdown?: boolean;
    isSelecting?: boolean;
    disabled?: boolean;
  }>(),
  { showTransferBreakdown: false, isSelecting: false, disabled: false },
);

const transferPriceTotal = computed(() => {
  if (!props.showTransferBreakdown) {
    return null;
  }
  return getTotalTransferPriceFromPricing(props.offer.pricing);
});

const emit = defineEmits<{
  (e: 'onOfferSelect', offer: Offer): void;
}>();
</script>

<i18n lang="json">
{
  "en": {
    "select": "Select",
    "availableFrom": "Available from",
    "vehiclePrice": "Vehicle price",
    "transferTotal": "Transfers"
  },
  "de": {
    "select": "Auswählen",
    "availableFrom": "Verfügbar ab",
    "vehiclePrice": "Fahrzeugpreis",
    "transferTotal": "Transfers"
  },
  "es": {
    "select": "Seleccionar",
    "availableFrom": "Disponible desde",
    "vehiclePrice": "Precio del vehículo",
    "transferTotal": "Traslados"
  }
}
</i18n>
